/**
 * Achar os links dentro do texto de um recado.
 *
 * ## Por que existe: a URL crua comia a prévia inteira
 *
 * Um recado real trouxe quatro links de Google Maps, cada um com mais de 200
 * caracteres de `%20` — as duas linhas de prévia viravam uma parede de escape
 * de URL, e nem o nome da loja aparecia. No texto inteiro não era melhor: a
 * linha quebrava no meio do `api=1&query=` e a informação que importa (loja,
 * preço, distância) ficava perdida entre eles.
 *
 * ## O texto é DADO, e não markup
 *
 * Quem escreve um recado é um agente ou uma pessoa colando o que um agente
 * devolveu. Renderizar isso como markup seria transformar conteúdo de terceiro
 * em HTML. Por isso o reconhecimento é o mais restrito possível: **só `http` e
 * `https`**, e nada mais — um `javascript:` no meio de um recado não vira link
 * clicável, vira texto.
 *
 * ## O rótulo é o HOST, e o host é o que decide
 *
 * Antes de tocar num link, o que se quer saber é para onde ele vai. O host
 * responde isso e não mente; o caminho, abreviado, mentiria. O primeiro
 * segmento entra só quando é curto e legível (`google.com/maps`), porque aí ele
 * diz que tipo de página é sem sugerir mais do que sabe.
 */

export type Pedaco =
  | { readonly tipo: 'texto'; readonly texto: string }
  | { readonly tipo: 'link'; readonly href: string; readonly rotulo: string }

/**
 * Só `http`/`https`, e o casamento para no primeiro espaço ou sinal de menor.
 * Pontuação final é devolvida ao texto — ver `aparar`.
 */
const URL_NO_TEXTO = /https?:\/\/[^\s<>"']+/gi

/**
 * Um ponto no fim de uma frase não é parte da URL, e um parêntese de fechamento
 * só é parte dela se houver um de abertura dentro — que é o caso do Wikipédia,
 * e o motivo de isto não ser um `replace` simples.
 */
function aparar(bruto: string): { url: string; sobra: string } {
  let url = bruto
  for (;;) {
    const ultimo = url.at(-1)
    if (ultimo === undefined) break
    if ('.,;:!?'.includes(ultimo)) {
      url = url.slice(0, -1)
      continue
    }
    if (ultimo === ')' && !url.includes('(')) {
      url = url.slice(0, -1)
      continue
    }
    break
  }
  return { url, sobra: bruto.slice(url.length) }
}

/** `https://www.google.com/maps/search/?api=1&query=…` → `google.com/maps`. */
export function rotuloDe(href: string): string {
  let u: URL
  try {
    u = new URL(href)
  } catch {
    // URL que o `URL` recusa mas a regex pegou. Mostrar o cru truncado é mais
    // honesto que inventar um rótulo bonito para um endereço quebrado.
    return href.length > 40 ? `${href.slice(0, 40)}…` : href
  }
  const host = u.hostname.replace(/^www\./, '')
  const primeiro = u.pathname.split('/').filter((x) => x !== '')[0] ?? ''
  // Curto e sem ponto: `maps` entra, `busca-de-produtos-por-categoria` não, e
  // `index.html` também não — um nome de arquivo não diz que tipo de página é.
  const cabe = primeiro.length > 0 && primeiro.length <= 12 && !primeiro.includes('.')
  return cabe ? `${host}/${primeiro}` : host
}

/** O texto partido em pedaços de texto e de link, na ordem em que aparecem. */
export function pedacosDe(texto: string): Pedaco[] {
  const pedacos: Pedaco[] = []
  let cursor = 0

  for (const achado of texto.matchAll(URL_NO_TEXTO)) {
    const inicio = achado.index
    const { url, sobra } = aparar(achado[0])
    if (inicio > cursor) pedacos.push({ tipo: 'texto', texto: texto.slice(cursor, inicio) })
    pedacos.push({ tipo: 'link', href: url, rotulo: rotuloDe(url) })
    if (sobra !== '') pedacos.push({ tipo: 'texto', texto: sobra })
    cursor = inicio + achado[0].length
  }

  if (cursor < texto.length) pedacos.push({ tipo: 'texto', texto: texto.slice(cursor) })
  return pedacos
}
